/**
 * Services Constants for Olvidadme
 *
 * Centralized service data following DRY principle from PATTERNS_FROM_BASKET_BASE.md
 * Single source of truth for all service-related data across the application
 *
 * This file contains:
 * - Service definitions with pricing and features
 * - Pricing plans for different tiers
 * - Value propositions and features
 *
 * Precios centralizados en: src/utils/constants/pricing.js
 * Following ADR-016: Centralized Constants Pattern
 */

import {
  DocumentTextIcon,
  ShieldExclamationIcon,
  HeartIcon
} from '@heroicons/vue/24/outline'

import { PRICING } from './pricing.js'

// Core service definitions
// ============================================================================
// ⚠️ MODELO DE NEGOCIO: SERVICIOS INDEPENDIENTES (NO ACUMULATIVOS)
// ============================================================================
//
// Los 3 servicios son PRODUCTOS DISTINTOS para NECESIDADES DISTINTAS:
// - NO es que un servicio "incluya" a otro
// - Cada servicio tiene un TARGET completamente diferente
// - 1 PAGO = 1 CASO (siempre)
//
// Mapeo servicio → tier backend:
// - derecho-olvido              → olvido            (499€) → Eliminar datos personales de internet
// - derecho-olvido-postmortem   → olvido_postmortem (699€) → Derecho al olvido de personas fallecidas
// - testamento-digital          → testamento        (599€) → Planificar legado digital en vida
//
// Soporte: Email básico para todos (sin tiempos garantizados)
// ============================================================================
export const SERVICES = [
  {
    id: 'derecho-olvido',
    tier: 'olvido',
    name: PRICING.OLVIDO.name,
    shortDescription: 'Elimina tu información personal de internet',
    description: 'Ejercemos tu derecho al olvido según GDPR Art. 17, eliminando datos personales de buscadores, redes sociales y sitios web. Nuestros especialistas gestionan todo el proceso legal para lograr la eliminación efectiva de tu información.',
    icon: ShieldExclamationIcon,
    price: PRICING.OLVIDO.price,
    popular: true,
    caseType: 'olvido',
    features: [
      'Análisis legal de viabilidad del caso',
      'Gestión de hasta 5 URLs',
      'Solicitudes GDPR a Google, Bing y otros buscadores',
      'Gestión en redes sociales (Facebook, Instagram, Twitter)',
      'Seguimiento y reclamaciones ante AEPD si es necesario',
      'Informes detallados de progreso',
      'Soporte por email'
    ],
    benefits: [
      'Protección efectiva de tu reputación online',
      'Cumplimiento legal garantizado según GDPR',
      'Proceso transparente con seguimiento detallado',
      'Expertise en derecho digital español y europeo'
    ]
  },
  {
    id: 'derecho-olvido-postmortem',
    tier: 'olvido_postmortem',
    name: PRICING.OLVIDO_POSTMORTEM.name,
    shortDescription: 'Protege el legado digital de personas fallecidas',
    description: 'Ejercemos el derecho al olvido de personas fallecidas según LOPDGDD y GDPR Art. 17, eliminando o memorializando sus datos en buscadores, redes sociales y plataformas digitales. Nuestros especialistas gestionan todo el proceso legal para que la familia pueda proteger el legado digital de su ser querido.',
    icon: HeartIcon,
    price: PRICING.OLVIDO_POSTMORTEM.price,
    popular: false,
    caseType: 'olvido_postmortem',
    features: [
      'Análisis legal de viabilidad del caso',
      'Identificación completa de presencia digital del fallecido',
      'Gestión de hasta 5 URLs',
      'Solicitudes GDPR a Google, Bing y otros buscadores',
      'Gestión de cuentas en redes sociales (Facebook, Instagram, Twitter, LinkedIn)',
      'Proceso de memorización de perfiles (convertir en memorial)',
      'Solicitudes de eliminación a todas las plataformas',
      'Recuperación segura de fotos y contenido digital',
      'Seguimiento y reclamaciones ante AEPD si es necesario',
      'Informes detallados de progreso',
      'Documentación legal completa para familiares',
      'Soporte prioritario durante todo el proceso'
    ],
    benefits: [
      'Proceso gestionado con máxima sensibilidad',
      'Cumplimiento total GDPR y legislación española',
      'Documentación completa para herederos',
      'Soporte dedicado para la familia'
    ]
  },
  {
    id: 'testamento-digital',
    tier: 'testamento',
    name: PRICING.TESTAMENTO.name,
    shortDescription: 'Planifica tu legado digital en vida',
    description: 'Crea tu testamento digital con instrucciones detalladas para tus herederos y/o albacea digital. Define qué hacer con tus cuentas, archivos y patrimonio digital cuando ya no estés.',
    icon: DocumentTextIcon,
    price: PRICING.TESTAMENTO.price,
    popular: false,
    caseType: 'testamento',
    features: [
      'Inventario completo de activos digitales',
      'Instrucciones detalladas para albacea digital',
      'Designación de albacea digital',
      'Documento legalmente válido',
      'Gestión de herederos digitales',
      'Instrucciones post-mortem personalizadas',
      'Asesoramiento legal incluido',
      'Soporte VIP'
    ],
    benefits: [
      'Tranquilidad sobre tu legado digital',
      'Documento legalmente reconocido',
      'Instrucciones claras para tus seres queridos',
      'Asesoramiento legal profesional'
    ]
  }
]

// Pricing plans - Derivados de SERVICES para consistencia
// Cada plan corresponde exactamente a un servicio (1:1)
// Precios importados de pricing.js
export const PRICING_PLANS = [
  {
    id: 'olvido',
    serviceId: 'derecho-olvido',
    name: PRICING.OLVIDO.name,
    price: PRICING.OLVIDO.price,
    period: 'Pago único',
    subtitle: 'Elimina tu información personal de internet',
    popular: true,
    features: [
      'Análisis legal de viabilidad del caso',
      'Gestión de hasta 5 URLs',
      'Solicitudes GDPR a buscadores y redes sociales',
      'Seguimiento y reclamaciones ante AEPD',
      'Soporte por email'
    ],
    idealFor: 'Personas que quieren eliminar su información personal de internet'
  },
  {
    id: 'olvido_postmortem',
    serviceId: 'derecho-olvido-postmortem',
    name: PRICING.OLVIDO_POSTMORTEM.name,
    price: PRICING.OLVIDO_POSTMORTEM.price,
    period: 'Pago único',
    subtitle: 'Protege el legado digital de personas fallecidas',
    popular: false,
    features: [
      'Identificación de presencia digital del fallecido',
      'Gestión de hasta 5 URLs',
      'Solicitudes GDPR a buscadores y plataformas',
      'Memorización o eliminación de perfiles',
      'Seguimiento y reclamaciones ante AEPD'
    ],
    idealFor: 'Familiares que necesitan gestionar el legado digital de un ser querido'
  },
  {
    id: 'testamento',
    serviceId: 'testamento-digital',
    name: PRICING.TESTAMENTO.name,
    price: PRICING.TESTAMENTO.price,
    period: 'Pago único',
    subtitle: 'Planifica tu legado digital en vida',
    popular: false,
    features: [
      'Inventario de activos digitales',
      'Designación de albacea digital',
      'Instrucciones post-mortem',
      'Documento legalmente válido',
      'Asesoramiento legal y soporte VIP'
    ],
    idealFor: 'Personas que quieren dejar instrucciones claras sobre su patrimonio digital'
  }
]

// Value propositions for hero sections
export const VALUE_PROPOSITIONS = [
  {
    id: 'payment',
    text: 'Sin pagos por adelantado'
  },
  {
    id: 'gdpr',
    text: 'GDPR 100% Compliant'
  }
]

// Core platform features with heroicons
export const PLATFORM_FEATURES = [
  {
    id: 'security',
    title: 'Máxima Seguridad',
    icon: 'LockClosedIcon',
    description: 'Encriptación AES-256 y protocolos de seguridad bancaria para proteger tus datos más sensibles'
  },
  {
    id: 'expertise',
    title: 'Expertise Legal',
    icon: 'ScaleIcon',
    description: 'Especialistas en derecho digital y jurisdicciones forales españolas con experiencia demostrada'
  },
  {
    id: 'process',
    title: 'Proceso Ágil',
    icon: 'RocketLaunchIcon',
    description: 'Gestión eficiente con seguimiento en tiempo real y comunicación transparente en cada paso'
  }
]

// Mapeo servicio → tier (para facilitar lookups)
// NOTA: Modelo 1:1 - cada servicio = un tier = un producto independiente
export const SERVICE_TO_TIER = {
  'derecho-olvido': 'olvido',
  'derecho-olvido-postmortem': 'olvido_postmortem',
  'testamento-digital': 'testamento'
}

// Mapeo inverso tier → servicio
export const TIER_TO_SERVICE = {
  'olvido': 'derecho-olvido',
  'olvido_postmortem': 'derecho-olvido-postmortem',
  'testamento': 'testamento-digital'
}

// Helper functions for service data
export const getServiceById = (id) => {
  return SERVICES.find(service => service.id === id)
}

export const getServiceByTier = (tier) => {
  return SERVICES.find(service => service.tier === tier)
}

export const getPlanById = (id) => {
  return PRICING_PLANS.find(plan => plan.id === id)
}

export const getPlanByServiceId = (serviceId) => {
  return PRICING_PLANS.find(plan => plan.serviceId === serviceId)
}

export const getTierForService = (serviceId) => {
  return SERVICE_TO_TIER[serviceId]
}

export const getServiceForTier = (tier) => {
  return TIER_TO_SERVICE[tier]
}

// Export default object for convenient destructuring
export default {
  SERVICES,
  PRICING_PLANS,
  VALUE_PROPOSITIONS,
  PLATFORM_FEATURES,
  SERVICE_TO_TIER,
  TIER_TO_SERVICE,
  getServiceById,
  getServiceByTier,
  getPlanById,
  getPlanByServiceId,
  getTierForService,
  getServiceForTier
}
